/**
 * Navigation state for the Settings TomeWindow (Stage 5 / Unification Plan).
 *
 * Tracks how Settings was opened ('gameMenu' | 'direct') so:
 * - When opened from the Esc menu (GameMenu), the top bar shows "← Game menu"
 *   and pressing Esc steps back to the Esc menu and restores focus on the
 *   "Settings" button.
 * - When closing, focus returns to the opening button if opened from the Esc menu.
 */

import { useSyncExternalStore } from 'react';
import { windowBridge } from '../../framework/windowBridge';
import { gameMenuStore } from '../../HUD/gameMenu/gameMenuStore';

export type SettingsOrigin = 'gameMenu' | 'direct';

let currentOrigin: SettingsOrigin = 'direct';
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

function focusSelectorSoon(selector: string): void {
  if (typeof document === 'undefined') return;
  const attempt = () => {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) el.focus();
  };
  attempt();
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(attempt);
  }
}

export const settingsNavigationStore = {
  getOrigin: (): SettingsOrigin => currentOrigin,
  setOrigin: (origin: SettingsOrigin): void => {
    if (currentOrigin === origin) return;
    currentOrigin = origin;
    notify();
  },
  openFromGameMenu: (): void => {
    currentOrigin = 'gameMenu';
    notify();
    gameMenuStore.close();
    windowBridge.open('settings');
  },
  toggleDirect: (): void => {
    currentOrigin = 'direct';
    notify();
    windowBridge.toggle('settings');
  },
  returnToGameMenu: (): void => {
    currentOrigin = 'direct';
    notify();
    windowBridge.close('settings');
    gameMenuStore.open();
    focusSelectorSoon('[data-testid="game-menu-settings"]');
  },
  restoreFocusOnClose: (originAtClose: SettingsOrigin = currentOrigin): void => {
    currentOrigin = 'direct';
    notify();
    if (originAtClose === 'gameMenu') {
      focusSelectorSoon('[data-testid="game-menu-settings"]');
    }
  },
  resetForTests: (): void => {
    currentOrigin = 'direct';
    notify();
  },
  subscribe: (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

export function useSettingsOrigin(): SettingsOrigin {
  return useSyncExternalStore(
    settingsNavigationStore.subscribe,
    settingsNavigationStore.getOrigin,
    settingsNavigationStore.getOrigin,
  );
}
